import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { t } from 'i18next';

import { LayoutType, InitScrollWithInput } from '../../../constants/LayoutConstants';
import { BaseComponent } from '../../BaseComponent';
import { AppConfigurationStore } from '../../../stores/AppConfigurationStore';
import { CatalogStore, CatalogStoreState } from '../../../stores/CatalogStore';
import { VisibilityStore } from '../../../stores/VisibilityStore';
import { DispatcherBaseAction } from '../../../dispatcher/AppDispatcher';
import { ActionConstants } from '../../../constants/ActionConstants';
import { UIHelper } from '../../../utils/UIHelper';
import { PopupMenu } from '../../../constants/Enums';
import { CatalogActions } from '../../../actions/CatalogActions';
import { CatalogSelectBox } from './CatalogSelectBox';
import { SidebarHeader } from '../../SidebarHeader';
import { ClickButton } from '../../ClickButton';
import { SelectBoxActions } from '../../../actions/SelectBoxActions';
import { CatalogItem } from './CatalogItem';
import { InputBox } from '../../InputBox';
import { VisibilityActions } from '../../../actions/VisibilityActions';
import CatalogLetterBar from './CatalogLetterBar';
import { CatalogNavigation } from './CatalogNavigation';

import eaiws = egr.wcf.eaiws;
import utils = egr.wcf.utils;

interface CatalogProps extends React.Props<Catalog> {
    visible: boolean;
    layoutType: LayoutType;
    rightSide: boolean;
    tooltipElement: JSX.Element;
}

interface CatalogState {
    catalogPath: Array<eaiws.catalog.CatalogItem>;
    catalogItems: Array<eaiws.catalog.CatalogItem>;
    filter: string | null;
    search: string | null;
    filterActive: boolean;
    popupOpen: boolean;
}

export class Catalog extends BaseComponent<CatalogProps, CatalogState> {
    protected defaultScrollPosition: number = (AppConfigurationStore.getState().inApp) ? InitScrollWithInput : 0;
    private inputFocus: boolean = false;

    /**
     * This var is used to store the scroll position for each catalog level.<br>
     * It should be posible to use multiple catalog components with different
     * scroll positions. Therefore a member variable is used instead of a store
     * property.
     */
    private scrollPosition: Array<number> = [];
    private scrollToLastPosition: boolean = true;

    public componentWillMount(): void {
        this.addStoreListener(CatalogStore, this.onChange);
        this.addStoreListener(VisibilityStore, this.onChange);
        this.addActionListener((action: DispatcherBaseAction): void => {
            // reset scroll position if a new project was started
            if (action.actionType === ActionConstants.RESET_PROJECT_RELATED_SETTINGS) {
                this.scrollPosition.length = 0;
                this.onChange();
            }
        });
        this.onChange();
    }

    public componentDidUpdate(): void {
        if (this.props.visible) {
            this.scrollPosition.length = this.state.catalogPath.length + 1;
            if (this.scrollPosition[this.state.catalogPath.length] == null) {
                this.scrollPosition[this.state.catalogPath.length] = this.defaultScrollPosition;
            }

            if (!utils.string.isNullOrEmpty(this.state.search)) {
                this.scrollPosition[this.state.catalogPath.length] = 0;
            }
            const forcedScrollPosition: number | null =
                (this.scrollToLastPosition || this.state.catalogPath.length === 0) ?
                    (this.inputFocus ? 0 : this.scrollPosition[this.state.catalogPath.length]) :
                    null;
            const scrollContainer: JQuery | undefined = UIHelper.updateScrollbar(
                ReactDOM.findDOMNode(this) as Element,
                (AppConfigurationStore.getState().inApp ? '.sidebar-content' : '.scrollbar-wcf'),
                forcedScrollPosition
            );

            if (forcedScrollPosition != null && scrollContainer != null) {
                this.updateScrollRelatedItems(forcedScrollPosition, scrollContainer);
            }
        }
    }

    public shouldComponentUpdate(nextProps: CatalogProps, nextState: CatalogState): boolean {
        if (
            this.state &&
            (
                nextState.catalogPath !== this.state.catalogPath ||
                nextProps.visible === true && this.props.visible === false
            )
        ) {
            this.scrollToLastPosition = true;
        }
        return true;
    }

    public render(): JSX.Element | null {
        if (!this.props.visible) {
            return null;
        }

        const lastCatalogPath: undefined | eaiws.catalog.CatalogItem = this.state.catalogPath[this.state.catalogPath.length - 1];
        let headerLabel: string = t('Catalogs');
        if (lastCatalogPath != null) {
            headerLabel = lastCatalogPath.label || lastCatalogPath.name;
        }

        const firstPage: boolean = this.state.catalogPath[0] == null;
        let letterBar: JSX.Element | null = null;
        if (
            lastCatalogPath == null &&
            AppConfigurationStore.getState().letterBarInCatalog &&
            !(this.props.layoutType !== LayoutType.DESKTOP && document.body.clientHeight < 545) &&
            !(this.props.layoutType === LayoutType.DESKTOP && document.body.clientHeight < 620)
        ) {
            letterBar = (
                <CatalogLetterBar
                    layoutType={this.props.layoutType}
                    scrollCallback={this.scrollTo}
                    inSide={AppConfigurationStore.getState().touchDevice}
                />
            );
        }
        const items: Array<JSX.Element | null> = this.catalogItems();
        // Special handling for WindowsApp, because we use custom scrollbar there
        if (AppConfigurationStore.getState().inApp && UIHelper.customScrollbarEnabled()) {
            return (
                <div className={'sidebar-tab-container' + (letterBar != null ? ' has-letterbar' : '')} key="catalog-list-desktop" id="catalog-tab">
                    {letterBar}
                    {letterBar != null ? null : <div className="scroll-to-top" onClick={this.scrollToTop}/>}
                    {this.header(headerLabel)}
                    <div className={'sidebar-content scrollbar-wcf' + (this.state.filterActive ? ' filter-active' : '')} onScroll={this.onScroll}>
                        {this.inputBox(firstPage)}
                        {items.length === 0 && !utils.string.isNullOrEmpty(this.state.search) ?
                            <div className="search-no-results">{t('No results found.')}</div> :
                            <div className="catalog-items" style={{paddingBottom: 10}}>{this.catalogItems()}</div>}
                    </div>
                </div>
            );
        }
        return (
            <div
                className={'sidebar-tab-container' + (letterBar != null ? ' has-letterbar' : '')}
                key="catalog-list-desktop"
                id="catalog-tab"
                onClick={this.closePopup}
            >
                {letterBar}
                {letterBar != null ? null : <div className="scroll-to-top" onClick={this.scrollToTop}/>}
                <div className="scrollbar-wcf" onScroll={this.onScroll}>
                    {this.header(headerLabel)}
                    <CatalogNavigation
                        catalogSelectBox={this.catalogSelectBox()}
                        inCatalog={!firstPage}
                        search={this.state.search != null && this.state.search.length > 0}
                        label={headerLabel}
                        key="catalog-navigation"
                    />
                    <div
                        className={
                            'sidebar-content' + (AppConfigurationStore.getState().inApp ? '' : ' catalog-header') +
                            (this.state.filterActive ? ' filter-active' : '')
                        }
                    >
                        {this.inputBox(firstPage)}
                        {items.length === 0 && !utils.string.isNullOrEmpty(this.state.search) ?
                            <div className="search-no-results">{t('No results found.')}</div> :
                            <div className="catalog-items">{this.catalogItems()}</div>}
                    </div>
                </div>
            </div>
        );
    }

    protected onChange = (): void => {
        const eaiwsState: CatalogStoreState = CatalogStore.getState();
        this.setState({
            catalogPath: eaiwsState.catalogPath,
            catalogItems: eaiwsState.catalogItems,
            filter: eaiwsState.filter,
            search: eaiwsState.search,
            filterActive: (
                (eaiwsState.catalogPath) &&
                (
                    (eaiwsState.catalogPath[0] == null && eaiwsState.filter != null && eaiwsState.filter !== '') ||
                    (eaiwsState.catalogPath[0] != null && eaiwsState.search !== '' && eaiwsState.search !== '')
                )
            ),
            popupOpen: VisibilityStore.getState().popupMenu === PopupMenu.CATALOG
        });
    };

    protected onItemClicked = (item: eaiws.catalog.CatalogItem): void => {
        CatalogActions.enterCatalogItem(item);
    };

    protected onBackClick = (): void => {
        CatalogActions.leaveCatalogItem();
    };

    protected searchChanged = (value: string | null): void => {
        // if user clears the search field after he did a search go to path before search
        if (utils.string.isNullOrEmpty(value) && !utils.string.isNullOrEmpty(this.state.search)) {
            CatalogActions.leaveCatalogItem();
        }
    };

    protected blur = (): void => {
        this.inputFocus = false;
    };

    protected focus = (): void => {
        jQuery(ReactDOM.findDOMNode(this)).find((AppConfigurationStore.getState().inApp ? '.sidebar-content' : '.scrollbar-wcf')).scrollTop(1).scroll();
        this.inputFocus = true;
    };

    protected catalogSelectBox = (): JSX.Element => {
        return (
            <CatalogSelectBox
                catalogPath={this.state.catalogPath}
                catalog={this.state.catalogPath[this.state.catalogPath.length - 1]}
                rightSide={this.props.rightSide}
                layoutType={this.props.layoutType}
            >
                {this.props.tooltipElement}
            </CatalogSelectBox>
        );
    };

    protected header = (label: string): JSX.Element => {
        if (this.state.catalogPath[this.state.catalogPath.length - 1] != null) {
            let headerLabelElement: JSX.Element;
            if (utils.string.isNullOrEmpty(this.state.search)) {
                headerLabelElement = <div className="catalog-header-label">{label}</div>;
            } else {
                headerLabelElement = (
                    <div className="catalog-search">
                        <div className="catalog-search-label">{t('Search in')}&nbsp;"{label}</div>
                        <div className="catalog-search-quote">"</div>
                    </div>
                );
            }
            if (AppConfigurationStore.getState().inApp) {
                return (
                    <SidebarHeader leftArrow={true} arrowCallback={this.onBackClick} text="">
                        {headerLabelElement}
                        {this.props.layoutType === LayoutType.SMALL ? (
                            <ClickButton
                                className="catalog-menu-button"
                                callback={() => SelectBoxActions.showCatalogMenu(label, this.state.catalogPath)}
                            >
                                {this.props.tooltipElement}
                            </ClickButton>
                        ) : this.catalogSelectBox()
                        }
                    </SidebarHeader>
                );
            }

            return <SidebarHeader text={t('Catalogs')}/>;
        }

        return (
            <SidebarHeader text={label} />
        );
    };

    protected scrollTo = (scrollTop: number): void => {
        if (AppConfigurationStore.getState().inApp) {
            if (this.state.filterActive) {
                jQuery(ReactDOM.findDOMNode(this)).find('.catalog-items').scrollTop(0).scroll();
            } else {
                jQuery(ReactDOM.findDOMNode(this)).find('.sidebar-content').scrollTop(scrollTop).scroll();
            }
        } else {
            jQuery(ReactDOM.findDOMNode(this)).find('.scrollbar-wcf').scrollTop(scrollTop).scroll();
        }
    };

    protected scrollToTop: () => void = () => {
        if (AppConfigurationStore.getState().inApp) {
            this.scrollTo(InitScrollWithInput);
        } else {
            this.scrollTo((this.props.layoutType === LayoutType.DESKTOP ? 89 : 58));
        }
    };

    protected updateScrollButton: (scrollTop: number) => void = (scrollTop: number) => {
        let breakPoint: number = (this.props.layoutType === LayoutType.DESKTOP ? 89 : 58);
        if (AppConfigurationStore.getState().inApp) {
            breakPoint = InitScrollWithInput;
        }

        if (scrollTop > breakPoint) {
            jQuery(ReactDOM.findDOMNode(this)).find('.scroll-to-top').show();
        } else {
            jQuery(ReactDOM.findDOMNode(this)).find('.scroll-to-top').hide();
        }
    };

    protected updateScrollRelatedItems = (scrollTop: number, target: JQuery): void => {
        this.updateScrollButton(scrollTop);
        if (!AppConfigurationStore.getState().inApp) {
            UIHelper.repositionFixedElements(
                scrollTop,
                (this.props.layoutType === LayoutType.DESKTOP ? 89 : 58),
                target,
                [
                    {class: '.catalog-navigation', toggleClass: 'fixed-in-sidebar'},
                    {class: '.input-container.in-sidebar-tab', toggleClass: 'fixed-in-sidebar'},
                ]
            );
        }
    };

    protected onScroll: any = (event: React.SyntheticEvent): void => {
        const target: HTMLElement = event.target as HTMLElement;
        if (target == null || target.scrollTop == null) {
            return;
        }

        if (
            this.scrollPosition[this.state.catalogPath.length] !== target.scrollTop &&
            this.scrollPosition[this.state.catalogPath.length] !== target.scrollTop + 1 &&
            this.scrollPosition[this.state.catalogPath.length] !== target.scrollTop - 1
        ) {
            this.scrollPosition[this.state.catalogPath.length] = target.scrollTop;
            this.updateScrollRelatedItems(target.scrollTop, jQuery(target));
        }
    };

    protected catalogItems = (): Array<JSX.Element | null> => {
        const catalog: eaiws.catalog.CatalogItem = this.state.catalogPath[0];
        let lastLetter: string | null = null;
        if (AppConfigurationStore.getState().inApp) {
            let tempList: Array<JSX.Element> = [];
            let lastType: string | null = null;
            const longButtonGroups: Array<JSX.Element> = [];
            this.state.catalogItems.map((catalogItem: eaiws.catalog.CatalogItem, index: number): void => {
                let label: string = catalogItem.label;
                if (label == null || label === '') {
                    label = catalogItem.name;
                }
                if (
                    (catalog != null || (!this.state.filter || label.toLocaleLowerCase().indexOf(this.state.filter.toLocaleLowerCase()) !== -1)) &&
                    CatalogActions.isValidItem(catalogItem)
                ) {
                    const catalogKey: string = this.state.catalogPath.concat(catalogItem).map((item: eaiws.catalog.CatalogItem): string => {
                        if (item.type === 'Article') {
                            return md5((item as eaiws.catalog.ArticleCatalogItem).baseArticleNumber + (item as eaiws.catalog.ArticleCatalogItem).variantCode);
                        }
                        return item.name;
                    }).join('|');
                    if (lastType == null) { lastType = catalogItem.type; }
                    if (lastType !== catalogItem.type) {
                        longButtonGroups.push(<div className="long-button-group">{tempList}</div>);
                        tempList = [(
                            <CatalogItem
                                anchor={lastLetter !== label[0].toLocaleLowerCase() ? label[0].toLocaleLowerCase() : null}
                                key={catalogKey}
                                catalogItem={catalogItem}
                                catalog={catalog}
                                clickHandler={this.onItemClicked}
                                addSpacer={false}
                                layoutType={this.props.layoutType}
                            />
                        )];
                        lastType = catalogItem.type;
                    } else {
                        tempList.push(
                            <CatalogItem
                                anchor={lastLetter !== label[0].toLocaleLowerCase() ? label[0].toLocaleLowerCase() : null}
                                key={catalogKey}
                                catalogItem={catalogItem}
                                catalog={catalog}
                                clickHandler={this.onItemClicked}
                                addSpacer={false}
                                layoutType={this.props.layoutType}
                            />
                        );
                    }
                    lastLetter = label[0].toLocaleLowerCase();
                }
            });
            if (tempList.length) {
                longButtonGroups.push(<div className="long-button-group">{tempList}</div>);
            }
            return longButtonGroups;
        }
        let previousType: eaiws.catalog.CatalogItemType | null = null;
        return this.state.catalogItems.map((catalogItem: any) => {
            let label: string = catalogItem.label;
            if (label == null || label === '') {
                label = catalogItem.name;
            }
            if (
                (catalog != null || (!this.state.filter || label.toLocaleLowerCase().indexOf(this.state.filter.toLocaleLowerCase()) !== -1)) &&
                CatalogActions.isValidItem(catalogItem)
            ) {
                const catalogKey: string = this.state.catalogPath.concat(catalogItem).map((item: eaiws.catalog.CatalogItem): string => {
                    if (item.type === 'Article') {
                        return md5((item as eaiws.catalog.ArticleCatalogItem).baseArticleNumber + (item as eaiws.catalog.ArticleCatalogItem).variantCode);
                    }
                    return item.name;
                }).join('|');
                const addSpacer: boolean = (previousType != null && catalogItem.type !== previousType);
                previousType = catalogItem.type;
                const anchor: string | null = (lastLetter !== label[0].toLocaleLowerCase() ? label[0].toLocaleLowerCase() : null);
                lastLetter = label[0].toLocaleLowerCase();
                return (
                    <CatalogItem
                        anchor={anchor}
                        key={catalogKey}
                        catalogItem={catalogItem}
                        catalog={catalog}
                        clickHandler={this.onItemClicked}
                        addSpacer={addSpacer}
                        layoutType={this.props.layoutType}
                    />
                );
            }
            return null;
        });
    };

    protected inputBox = (firstPage: boolean): JSX.Element => {
        let className: string = 'filter-input';
        if (!firstPage) {
            className = 'search-input';
            if (!utils.string.isNullOrEmpty(this.state.search)) {
                className += ' active changed';
            }
        }
        return (
            <InputBox
                className={className}
                defaultValue={firstPage ? this.state.filter : this.state.search}
                placeholder={firstPage ? t('Filter') : t('Search')}
                changeCallback={firstPage ? CatalogActions.setFilter : this.searchChanged}
                submitCallback={!firstPage ? CatalogActions.searchCatalog : undefined}
                onBlur={this.blur}
                onFocus={this.focus}
                position={'in-sidebar-tab'}
                key={firstPage ? 'filter-input' : 'search-input'}
            />
        );
    };

    protected closePopup = (): void => {
        if (this.state.popupOpen) {
            VisibilityActions.hidePopupMenu(PopupMenu.CATALOG);
        }
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/sidebar/catalog/Catalog.tsx